import { type AnnouncementContentVerticalAlign } from '@components/announcement/AnnouncementContentVerticalAlign';
import { type useAnnouncementItemData } from '@components/announcement/hooks/useAnnouncementItemData.hooks';
import {
  type AnnouncementItemEditorState,
  toAnnouncementItemEditorState,
} from '@components/announcement/util/announcementItemEditorState.util';
import { AnchorXDTO } from '@model/dynamicTimeTable/AnchorXDTO';
import { AnchorYDTO } from '@model/dynamicTimeTable/AnchorYDTO';
import { ColorDTO } from '@model/dynamicTimeTable/ColorDTO';
import { useRef, useState } from 'react';
import { ImageResizeType } from '@/model';

interface UseAnnouncementItemEditorStateProps {
  existingItem: ReturnType<typeof useAnnouncementItemData>['existingItem'];
  fetchedAnnouncement: ReturnType<typeof useAnnouncementItemData>['fetchedAnnouncement'];
  onDirty: () => void;
}

interface UseAnnouncementItemEditorStateResult {
  editorState: AnnouncementItemEditorState;
  updateEditorState: (patch: Partial<AnnouncementItemEditorState>) => void;
  contentBoxRef: React.RefObject<HTMLDivElement>;
  onNameChange: (value: string) => void;
  onTemplateChange: (value: boolean) => void;
  onXChange: (value: number) => void;
  onYChange: (value: number) => void;
  onImageResizeTypeChange: (value: ImageResizeType) => void;
  onBackgroundHorizontalAlignChange: (value: AnchorXDTO) => void;
  onBackgroundVerticalAlignChange: (value: AnchorYDTO) => void;
  onBackgroundColorChange: (value: ColorDTO) => void;
  onVerticalAlign: (align: AnnouncementContentVerticalAlign) => void;
}

// The item's mutable layout/style state (position, size, background) and
// every way it can be patched — direct field changes, and the "align
// vertically within the canvas" shortcut, which needs the rendered content's
// height to compute a y offset.
export const useAnnouncementItemEditorState = ({
  existingItem,
  fetchedAnnouncement,
  onDirty,
}: UseAnnouncementItemEditorStateProps): UseAnnouncementItemEditorStateResult => {
  const [editorState, setEditorState] = useState<AnnouncementItemEditorState>(() =>
    toAnnouncementItemEditorState(existingItem, fetchedAnnouncement),
  );
  const contentBoxRef = useRef<HTMLDivElement>(null);

  const updateEditorState = (patch: Partial<AnnouncementItemEditorState>) => {
    setEditorState((prev) => ({ ...prev, ...patch }));
    onDirty();
  };

  const onVerticalAlign = (align: AnnouncementContentVerticalAlign) => {
    const contentHeight = contentBoxRef.current?.offsetHeight ?? 0;
    const availableSpace = editorState.height - contentHeight;

    switch (align) {
      case 'top':
        updateEditorState({ y: 0 });
        break;
      case 'center':
        updateEditorState({ y: Math.floor(availableSpace / 2) });
        break;
      case 'bottom':
        updateEditorState({ y: availableSpace });
        break;
    }
  };

  return {
    editorState,
    updateEditorState,
    contentBoxRef,
    onNameChange: (value) => updateEditorState({ name: value }),
    onTemplateChange: (value) => updateEditorState({ template: value }),
    onXChange: (value) => updateEditorState({ x: value }),
    onYChange: (value) => updateEditorState({ y: value }),
    onImageResizeTypeChange: (value) => updateEditorState({ imageResizeType: value }),
    onBackgroundHorizontalAlignChange: (value) =>
      updateEditorState({ backgroundHorizontalAlign: value }),
    onBackgroundVerticalAlignChange: (value) =>
      updateEditorState({ backgroundVerticalAlign: value }),
    onBackgroundColorChange: (value) => updateEditorState({ backgroundColor: value }),
    onVerticalAlign,
  };
};
